import { useEffect, useCallback, useRef, useState } from "react";
import { useAuth } from "./useAuth";
import { useToast } from "./use-toast";
import {
  SESSION_DURATIONS,
  INACTIVITY_TIMEOUT,
  WARNING_BEFORE_TIMEOUT,
  SESSION_START_KEY,
  LAST_ACTIVITY_KEY,
  REMEMBER_ME_KEY,
  SESSION_EXPIRED_KEY,
} from "@/lib/session-storage";

// Re-export utilities for convenience
export {
  setRememberMe,
  getRememberMe,
  initializeSession,
  clearSessionData,
} from "@/lib/session-storage";

export type SessionExpirationReason = "duration" | "inactivity" | null;

interface UseSessionManagerResult {
  showWarning: boolean;
  warningSecondsLeft: number;
  extendSession: () => void;
  sessionExpired: SessionExpirationReason;
  clearExpiredFlag: () => void;
}

export const useSessionManager = (): UseSessionManagerResult => {
  const { user, role, signOut } = useAuth();
  const { toast } = useToast();
  
  const [showWarning, setShowWarning] = useState(false);
  const [warningSecondsLeft, setWarningSecondsLeft] = useState(120);
  const [sessionExpired, setSessionExpired] = useState<SessionExpirationReason>(null);
  
  const activityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Get the maximum session duration based on role and remember me preference
  const getMaxSessionDuration = useCallback((): number => {
    const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === "true";
    
    if (rememberMe) {
      return SESSION_DURATIONS.remember_me;
    }
    
    // Check if user is admin via the admin hook pattern
    // For now, employers and admins get the same shorter duration
    if (role === "student") {
      return SESSION_DURATIONS.student;
    }
    // employer role (admins are also employers in this system)
    return SESSION_DURATIONS.employer;
  }, [role]);

  // Clear all timeouts
  const clearAllTimeouts = useCallback(() => {
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
      activityTimeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
      sessionCheckIntervalRef.current = null;
    }
  }, []);

  // Handle session expiration
  const handleSessionExpire = useCallback(async (reason: SessionExpirationReason) => {
    clearAllTimeouts();
    setShowWarning(false);
    
    // Store the expiration reason for display after redirect
    localStorage.setItem(SESSION_EXPIRED_KEY, reason || "");
    
    // Clean up session data
    localStorage.removeItem(SESSION_START_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    
    // Sign out the user
    await signOut();
    
    setSessionExpired(reason);
  }, [clearAllTimeouts, signOut]);

  // Update last activity timestamp
  const updateLastActivity = useCallback(() => {
    if (!user) return;
    
    const now = Date.now();
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    
    // Reset inactivity warning
    setShowWarning(false);
    
    // Clear existing timeouts
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
    }
    
    // Set warning timeout (43 minutes - 2 minutes before expiry)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setWarningSecondsLeft(120);
      
      // Start countdown
      warningIntervalRef.current = setInterval(() => {
        setWarningSecondsLeft(prev => {
          if (prev <= 1) {
            if (warningIntervalRef.current) {
              clearInterval(warningIntervalRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT);
    
    // Set actual expiry timeout (45 minutes)
    activityTimeoutRef.current = setTimeout(() => {
      handleSessionExpire("inactivity");
    }, INACTIVITY_TIMEOUT);
  }, [user, handleSessionExpire]);

  // Extend session (when user clicks "Stay connected")
  const extendSession = useCallback(() => {
    setShowWarning(false);
    updateLastActivity();
    
    toast({
      title: "Session prolongée",
      description: "Votre session a été prolongée avec succès.",
    });
  }, [updateLastActivity, toast]);

  // Clear the expired flag (called after showing the message)
  const clearExpiredFlag = useCallback(() => {
    localStorage.removeItem(SESSION_EXPIRED_KEY);
    setSessionExpired(null);
  }, []);

  // Check for stored expiration reason on mount
  useEffect(() => {
    const storedReason = localStorage.getItem(SESSION_EXPIRED_KEY) as SessionExpirationReason;
    if (storedReason) {
      setSessionExpired(storedReason);
    }
  }, []);

  // Initialize session tracking when user logs in
  useEffect(() => {
    if (!user) {
      clearAllTimeouts();
      return;
    }

    // Initialize session start time if not set
    const sessionStart = localStorage.getItem(SESSION_START_KEY);
    if (!sessionStart) {
      localStorage.setItem(SESSION_START_KEY, Date.now().toString());
    }

    // Initialize last activity
    updateLastActivity();

    // Set up activity listeners
    const activityEvents = ["mousedown", "keydown", "touchstart", "scroll", "mousemove"];
    
    let throttleTimeout: ReturnType<typeof setTimeout> | null = null;
    const throttledUpdate = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          updateLastActivity();
          throttleTimeout = null;
        }, 30000); // Throttle to every 30 seconds
      }
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, throttledUpdate, { passive: true });
    });

    // Check session duration periodically (every minute)
    sessionCheckIntervalRef.current = setInterval(() => {
      const startTime = parseInt(localStorage.getItem(SESSION_START_KEY) || "0", 10);
      const maxDuration = getMaxSessionDuration();
      const now = Date.now();

      if (startTime && now - startTime > maxDuration) {
        handleSessionExpire("duration");
      }
    }, 60000);

    // Initial check
    const startTime = parseInt(localStorage.getItem(SESSION_START_KEY) || "0", 10);
    const maxDuration = getMaxSessionDuration();
    if (startTime && Date.now() - startTime > maxDuration) {
      handleSessionExpire("duration");
    }

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, throttledUpdate);
      });
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      clearAllTimeouts();
    };
  }, [user, updateLastActivity, getMaxSessionDuration, handleSessionExpire, clearAllTimeouts]);

  return {
    showWarning,
    warningSecondsLeft,
    extendSession,
    sessionExpired,
    clearExpiredFlag,
  };
};
